import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, Method} from 'axios';
import {InteractionRequiredAuthError} from '@azure/msal-browser';
import {Buffer} from 'buffer';
import {ApiKeys, ApiService} from '../constants';
import {loginRequest, msalInstance} from './AzureAD_Util';
import Swal from 'sweetalert2';
import moment from 'moment';

interface IAxiosInstance {
  controller: AbortController,
  instance: AxiosInstance
}

export enum AxiosIdOperation {
  Current,
  Next,
  Before
}

export const encodeBase64DivID = (): string | null => {
  try {
    return Buffer.from(ApiKeys.icdc_division_id).toString(
        'base64'
    );
  } catch (err) {
    console.error(err);
    return null;
  }
};

const authorizationTypes = {
  BASIC: 'Basic',
  BEARER: 'Bearer'
};

const getAuthenticatedHeader = (
    authorizationType: string,
    token: string | null,
    msalToken: string,
    headerContentType?: string) => {
  let parseHeaders = {
    'Content-Type': headerContentType ? headerContentType : 'application/json',
    BinusAuthorization: '',
    Authorization: '',
    'Binusian-Id': '',
    Email: ''
  };

  let binusianID = '';
  let email = '';

  const base64DivID = encodeBase64DivID();

  if(token == null && msalToken != null) {
    parseHeaders = {
      ...parseHeaders,
      Authorization: `${authorizationTypes.BEARER} ${msalToken}`,
      BinusAuthorization: `${authorizationTypes.BASIC} ${base64DivID}`
    };
  } else if(token == null) {
    parseHeaders = {
      ...parseHeaders,
      Authorization: `${authorizationTypes.BASIC} ${base64DivID}`,
    };
  } else {
    const jsonString = localStorage.getItem('persist:root');

    if (jsonString != null) {
      const object = JSON.parse(jsonString);

      if (JSON.parse(object.auth).userData) {
        binusianID = JSON.parse(object.auth).userData.binusianId;
        email = JSON.parse(object.auth).userData.email;
      }
    }

    parseHeaders = {
      ...parseHeaders,
      Authorization: `${authorizationType} ${token}`,
      BinusAuthorization: `${authorizationTypes.BASIC} ${base64DivID}`,
      Email: `${email}`,
      'Binusian-Id': `${binusianID}`
    };
  }

  return parseHeaders;
};

const getMsalToken = async (): Promise<string> => {
  const msalAccount = msalInstance.getActiveAccount();

  if(!msalAccount){
    return null
  }

  let msalRequestConfig = loginRequest
  msalRequestConfig.prompt = null;
  msalRequestConfig.loginHint = getUserData()?.userName;

  try {
    let token = (await msalInstance.acquireTokenSilent(msalRequestConfig)).accessToken;

    if(!tokenDecodeValidityCheck(token, true)) {
      throw new Error("Msal Token invalid")
    }

    return token;
  } catch (error) {
    await msalInstance.loginRedirect(msalRequestConfig);
  }
}


const getSignedRequestOptions = async (
  method: Method,
  data: any,
  authorizationType: string,
  token: string | null,
  headerContentType?: string
) => {
  let parseHeaders = getAuthenticatedHeader(authorizationType, token, await getMsalToken(), headerContentType);

  let requestOptions: AxiosRequestConfig = {
    method,
    data,
    headers: parseHeaders
  };

  return requestOptions;
};

const getErrorMessageDisplay = (errorMessages: any) => {
  let result = '';
  if (errorMessages && errorMessages.length > 0) {
    for (const i in errorMessages) {
      result += '<li style="text-align: left;">' + errorMessages[i] + '</li>';
    }
  }
  if (result != '') {
    result = '<ul>' + result + '</ul>';
  }
  return result;
};


let axiosInstances= {}

const createAxiosInstance = (): IAxiosInstance => {
  const controller = new AbortController()
  return {
    controller,
    instance: axios.create({
      signal: controller.signal
    })
  }
}

let axiosInstantHelper = {
  previousPath: "",
  maximumAxiosId: 10,
  axiosCounter: 0
}

const handleMod = function (a, b) {
  return ((a % b) + b) % b;
};

export const handleAxiosId = (operation: AxiosIdOperation): string => {
  switch (operation){
    case AxiosIdOperation.Current:
      return (axiosInstantHelper.axiosCounter % axiosInstantHelper.maximumAxiosId).toString()
    case AxiosIdOperation.Before:
      return handleMod((axiosInstantHelper.axiosCounter - 1), axiosInstantHelper.maximumAxiosId).toString()
    case AxiosIdOperation.Next:
      axiosInstantHelper.axiosCounter = handleMod((axiosInstantHelper.axiosCounter + 1), axiosInstantHelper.maximumAxiosId)
      return axiosInstantHelper.axiosCounter.toString()
  }
}

const getAxiosInstance = (axiosId: string): AxiosInstance => {
  if (axiosInstances[axiosId] && !axiosInstances[axiosId].controller.signal.aborted) {
    return axiosInstances[axiosId].instance
  }
  axiosInstances[axiosId] = createAxiosInstance()
  return axiosInstances[axiosId].instance
}

export const abortAxiosInstance = (axiosId: string) => {
  axiosInstances[axiosId].controller.abort()
}

let refreshTokenDialogInstance = null;
let underLogout = false;

export const clearSessionData = () => {
  underLogout = true;
  localStorage.clear();
  sessionStorage.clear();
  const cookies = document.cookie.split(";");

  cookies.forEach((cookie) => {
    const cookieName = cookie.split("=")[0].trim();
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=yourdomain.com; secure; samesite=strict`;
  });
}

const forceLogout = async () => {
  clearSessionData();

  const confirmation = await Swal.fire({
    text: `Your session has expired. Please click OK to log in again.`,
    title: `Token Session Expired`,
    icon: 'warning',
    allowOutsideClick: false,
    allowEscapeKey: false
  });

  if (confirmation.value){
    window.location.href = "/logout";
  }
}

const decodeJwt = (token: string) => {
  const parts = token.split('.');

  function base64UrlDecode(base64Url: string) {
    base64Url = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    while (base64Url.length % 4) {
      base64Url += '=';
    }
    return JSON.parse(atob(base64Url));
  }

  const header = base64UrlDecode(parts[0]);
  const payload = base64UrlDecode(parts[1]);

  return {
    header,
    payload,
  };
}

const tokenDecodeValidityCheck = (token: string, isMicrosoft: boolean = false): boolean => {
  if(!token || token.trim().length == 0){
    return false;
  }

  let tokenParts: { payload: any; header?: any; };

  try{
    tokenParts = decodeJwt(token);
  } catch (_) {
    return false;
  }

  if(!isMicrosoft){
    return !(!tokenParts.payload.BinusianId);
  } else {
    return !(!tokenParts.payload.unique_name);
  }
}

const doRefreshToken = async (errorMessage) => {
  console.error(errorMessage)

  let expiredTokenMoment = moment(
      localStorage.getItem('expiredToken'),
      'YYYY-MM-DD HH:mm:ss'
  )

  let currentToken = JSON.parse(originLocalStorage.getItem())?.token
  let isTokenValid = tokenDecodeValidityCheck(currentToken)

  if (!isTokenValid || expiredTokenMoment.isBefore(moment().subtract(1, 'days'))) {
    await forceLogout();
  } else {
    expiredTokenMoment = moment().subtract(10, 'minutes');
    localStorage.setItem('expiredToken', expiredTokenMoment.format('YYYY-MM-DD HH:mm:ss'));

    const confirmation = await Swal.fire({
      text: `Your session has expired, if you click button OK your session will be extended`,
      title: `Session Expired`,
      icon: 'warning',
      allowOutsideClick: false,
      allowEscapeKey: false
    });

    if (confirmation.value){
      refreshTokenDialogInstance = null;
      await HttpService.getTokenAuth();
      window.location.reload();
    }
  }
}

const doFetch = async (url: string, requestOptions: AxiosRequestConfig) => {

  // Handle new instances
  let currentPath = window.location.pathname;

  if(!axiosInstantHelper.previousPath) {
    axiosInstantHelper.previousPath = currentPath
  }

  if(axiosInstantHelper.previousPath !== currentPath) {
    handleAxiosId(AxiosIdOperation.Next)
    abortAxiosInstance(handleAxiosId(AxiosIdOperation.Before))
    axiosInstantHelper.previousPath = currentPath
  }

  const axiosInstance = getAxiosInstance(handleAxiosId(AxiosIdOperation.Current))
  return axiosInstance(url, requestOptions)
    .then((result: AxiosResponse<any,any>) => {
      return result;
    })
    .catch((err) => {
      if(axios.isCancel(err)){
        return {
          statusText: "canceled",
          data: null
        } as AxiosResponse<any, any>
      }

      const error = err as AxiosError<any, any>;
      let errorMessage = error.response?.data;

      if (!errorMessage) {
        console.error(err);
      } else {
        if (errorMessage.ResultCode == 400) {
          if (errorMessage.errorMessages) {
            Swal.fire({
              html:
                errorMessage.errorMessages &&
                errorMessage.errorMessages.length > 1
                  ? getErrorMessageDisplay(errorMessage.errorMessages)
                  : errorMessage.allErrorMessage || errorMessage.errorMessage,
              title: '',
              icon: 'warning'
            });
            return errorMessage;
          }

          Swal.fire({
            html: 'An error has occured, please try again later.',
            title: `${errorMessage.errorMessage}`,
            icon: 'warning'
          });
          return errorMessage;
        }

        if (errorMessage.ResultCode == 500) {
          Swal.fire({
            html: 'An error has occured, please try again later.',
            title: `${errorMessage.errorMessage}`,
            icon: 'warning'
          });
        }

        if (errorMessage.ResultCode == 403 || errorMessage.ResultCode == 401) {
          if (!refreshTokenDialogInstance){
            refreshTokenDialogInstance = doRefreshToken(errorMessage.ErrorMessage)
          }
        }

        if (error.response.status == 400) {
          errorMessage.resultCode = 400;
        }
      }
      console.log('errorMessage', errorMessage);
      return errorMessage;
    });
};

export const originLocalStorage = {
  key: window.location.origin,
  setItem: function (value: any) {
    return localStorage.setItem(this.key, JSON.stringify(value));
  },
  getItem: function () {
    return localStorage.getItem(this.key);
  },
  removeItem: async function () {
    return localStorage.removeItem(this.key);
  },
  removePersist: async function () {
    return localStorage.removeItem('persist:root');
  }
};

const getUserData: any = () => {
  const jsonString = localStorage.getItem('persist:root');
  const object = JSON.parse(jsonString);
  try {
    return JSON.parse(object.auth).userData;
  }
  catch (_) {
    return null
  }
};

let tokenRefetcherInstance = null;

const tokenRefetcher = async (): Promise<AxiosResponse> => {
  let tokenUrl: string;
  let method: Method;
  let authTypes: string = authorizationTypes.BEARER;
  let token: string

  let localStorageData = originLocalStorage.getItem();

  try {
    token = JSON.parse(localStorageData).token;
  } catch (error) {
    await forceLogout();
  }

  let tokenValid: boolean;

  try {
    tokenValid = tokenDecodeValidityCheck(token);
  } catch (error) {
    tokenValid = false;
  }

  if(!tokenValid) {
    await forceLogout();
  }

  if(msalInstance.getActiveAccount()){
    tokenUrl = ApiService.icdc.auth.getToken
    method = 'GET';
    token = null;
  } else {
    tokenUrl = ApiService.icdc.auth.refreshToken
    method = 'POST';
  }

  const requestOptions = await getSignedRequestOptions(
      method,
      null,
      authTypes,
      token
  );

  return doFetch(
      tokenUrl,
      requestOptions
  ).then(async (result) => {
    if (result.errorMessage && result.errorMessage.trim()) {
      await forceLogout();
      return result;
    } else {
      originLocalStorage.setItem(result.data.tokenData);
      let _expiredToken = moment().add(
          result.data.tokenData.duration * 60 - 10,
          'seconds'
      ); // minus 10 second for delay get request token
      localStorage.setItem(
          'expiredToken',
          _expiredToken.format('YYYY-MM-DD HH:mm:ss')
      );
      return result;
    }
  });
}


export const HttpService = {
  async getAuthorizationToken<T = any>(
      url: string,
      username: string,
      password: string
  ): Promise<AxiosResponse<T>> {
    const requestOptions = await getSignedRequestOptions(
        password ? 'POST' : 'GET',
        password ? {userName: username, password: password} : null,
        authorizationTypes.BASIC,
        null
    );

    return doFetch(url, requestOptions)
        .then((result) => {
      if (result.errorMessage && result.errorMessage.trim()) {
        Swal.fire({
          text: `Error: ${result.errorMessage.trim()}`,
          title: `Login Failed`,
          icon: 'warning',
          allowOutsideClick: false,
          allowEscapeKey: false
        }).then(() => {
          clearSessionData();
          window.location.reload();
        })
      } else {
        originLocalStorage.setItem(result.data.tokenData);

        try {
          let _expiredToken = moment().add(
              result.data.tokenData.duration * 60 - 10,
              'seconds'
          );
          localStorage.setItem(
              'expiredToken',
              _expiredToken.format('YYYY-MM-DD HH:mm:ss')
          );
        } catch (error) {
        }

        return HttpService.post(ApiService.icdc.auth.userAuth, {
          userName: username
        }).then((resp) => {
          return resp;
        });
      }
    });
  },
  async getTokenAuth<T = any>(): Promise<AxiosResponse<T>> {
    if(window.location.pathname === '/logout' || underLogout || !originLocalStorage.getItem()){
      return null;
    }

    if(tokenRefetcherInstance && tokenRefetcherInstance.time.isAfter(moment().subtract(5, 'minutes'))) {
      return await tokenRefetcherInstance.instance;
    } else {
      tokenRefetcherInstance = {
        time: moment(),
        instance: tokenRefetcher()
      }
      return await tokenRefetcherInstance.instance;
    }
  },
  async get<T = any>(url: string): Promise<AxiosResponse<T>> {
    await checkToken();

    const token = JSON.parse(originLocalStorage.getItem())?.token

    const requestOptions = await getSignedRequestOptions(
      'GET',
      null,
      authorizationTypes.BEARER,
      token
    );
    return doFetch(url, requestOptions);
  },
  async post<T = any>(url: string, data: unknown, headerContentType?: string): Promise<AxiosResponse<T>> {
    await checkToken();

    const token = JSON.parse(originLocalStorage.getItem())?.token

    const requestOptions = await getSignedRequestOptions(
      'POST',
      data,
      authorizationTypes.BEARER,
      token,
      headerContentType
    );
    return doFetch(url, requestOptions);
  },

  async getUnSave<T = any>(url: string): Promise<AxiosResponse<T>> {
    let requestOptions: AxiosRequestConfig = { method: 'GET', data: null };
    return doFetch(url, requestOptions);
  },
  async postForm<T = any>(
    url: string,
    data: any,
    headers: any
  ): Promise<AxiosResponse<T>> {
    if (!headers || headers.length <= 0) {
      headers = {};
    }
    const body = new URLSearchParams();
    if (data) {
      const propertyNames = Object.keys(data);
      for (const ix in propertyNames) {
        body.append(propertyNames[ix], data[propertyNames[ix]]);
      }
    }

    let requestOptions: AxiosRequestConfig = {
      method: 'POST',
      data: body,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...headers
      }
    };

    return doFetch(url, requestOptions);
  },

  goToBeelajar(email: string, targetUrl: string) {
    const requetsPostRequestLogin = (email, callback) => {
      HttpService.postForm(
        ApiService.beelajar.postRequestLogin,
        {
          wstoken: ApiKeys.beelajarToken2,
          wsfunction: 'auth_userkey_request_login_url',
          moodlewsrestformat: 'json',
          'user[email]': email
        },
        null
      ).then((resp) => {
        if (resp.status === 200) {
          callback(resp.data);
        } else {
          console.error(resp);
        }
      });
    };

    requetsPostRequestLogin(email, (data) => {
      // console.log('data', data);
      if (!data.loginurl || data.loginurl == '') {
        alert('User Unauthorized');
        return;
      }
      window.open(data.loginurl, 'beelajarPage');
      setTimeout(() => {
        requetsPostRequestLogin(email, (data) => {
          window.open(data.loginurl + '&wantsurl=' + targetUrl, 'beelajarPage');
        });
      }, 500);
    });
  },

  onClickToDoBeelajar(todo: any) {
    const requetsPostRequestLogin = (email, callback) => {
      HttpService.postForm(
        ApiService.beelajar.postRequestLogin,
        {
          wstoken: ApiKeys.beelajarToken2,
          wsfunction: 'auth_userkey_request_login_url',
          moodlewsrestformat: 'json',
          'user[email]': email
        },
        null
      ).then((resp) => {
        if (resp.status === 200) {
          callback(resp.data);
        } else {
          console.error(resp);
        }
      });
    };

    HttpService.post(
      ApiService.icdc.module.notification.setToDoNotificationReadStatus,
      todo
    )
      .then((resp) => {
        requetsPostRequestLogin(resp.data.data.email, (data) => {
          if (!data.loginurl || data.loginurl == '') {
            alert('User Unauthorized');
            return;
          }
          window.open(data.loginurl, 'beelajarPage');
          setTimeout(() => {
            requetsPostRequestLogin(resp.data.data.email, (data) => {
              window.open(
                data.loginurl + '&wantsurl=' + resp.data.data.url,
                'beelajarPage'
              );
            });
          }, 500);
        });
      })
      .catch(() => {});
  },

  downloadFromBeelajar(email: string, targetUrl: string) {
    const requetsPostRequestLogin = (email, callback) => {
      HttpService.postForm(
        ApiService.beelajar.postRequestLogin,
        {
          wstoken: ApiKeys.beelajarToken2,
          wsfunction: 'auth_userkey_request_login_url',
          moodlewsrestformat: 'json',
          'user[email]': email
        },
        null
      ).then((resp) => {
        if (resp.status === 200) {
          callback(resp.data);
        } else {
          console.error(resp);
        }
      });
    };

    requetsPostRequestLogin(email, (data) => {
      setTimeout(() => {
        requetsPostRequestLogin(email, (data) => {
          window.open(data.loginurl + '&wantsurl=' + targetUrl, 'beelajarPage');
        });
      }, 500);
    });
  },
  goToBclnDFrontEnd(accessToken) {
    const mapForm = document.createElement('form');
    mapForm.target = '_blank';
    mapForm.method = 'POST';
    mapForm.action = ApiService.bclndFrontEnd.doLogin;

    // Create an input
    const mapInput = document.createElement('input');
    mapInput.type = 'text';
    mapInput.name = 'access_token';
    mapInput.value = accessToken;

    mapForm.appendChild(mapInput);
    document.body.appendChild(mapForm);
    mapForm.submit();
  },

  goToBclnDOldBackEnd(accessToken) {
    window.open(
      ApiService.bclndOldBackEnd.doLogin + '?FLAG=1&Token=' + accessToken,
      'BclnDOldEnd'
    );
  },

  goToBclnDNewBackEnd(accessToken) {
    const mapForm = document.createElement('form');
    mapForm.target = '_blank';
    mapForm.method = 'POST';
    mapForm.action = ApiService.bclndNewBackEnd.doLogin;

    // Create an input
    const mapInput = document.createElement('input');
    mapInput.type = 'text';
    mapInput.name = 'access_token';
    mapInput.value = accessToken;

    mapForm.appendChild(mapInput);
    document.body.appendChild(mapForm);
    mapForm.submit();
  },
  goToFma(userName, accessToken) {
    window.open(ApiService.fma.doLogin + '&username=' + userName, 'Fma');
  },
  goToLearningPath(userName) {
    window.open(ApiService.learningPath.doLogin + userName);
  }
};

const checkToken = async () => {
  if (JSON.parse(localStorage.getItem('persist:root'))) {
    if (JSON.parse(JSON.parse(localStorage.getItem('persist:root')).auth)) {
      var userData = JSON.parse(
        JSON.parse(localStorage.getItem('persist:root')).auth
      ).userData;
      if (userData) {
        let now = moment();
        let expiredToken = (
          localStorage.getItem('expiredToken')
            ? moment(
                localStorage.getItem('expiredToken'),
                'YYYY-MM-DD HH:mm:ss'
              )
            : now
        ).add(-5, 'seconds');
        await validateToken(expiredToken);
      }
    }
  }
};

const validateToken = async (
  expiredToken: moment.Moment | null,
  callback = undefined
) => {
  if (expiredToken) {
    if(expiredToken.isBefore(moment().subtract(1, 'days'))){ //logout if token is more than a day
      console.log("Session too old! Logging out!");
      await forceLogout();
    } else if (expiredToken.diff(moment(), 'seconds') <= 60) { //Refresh token
      console.log("Token expired! Getting new token!");
      await HttpService.getTokenAuth();
      if (typeof callback === 'function') {
        await callback();
      }
    }
  }
};

export const refreshTokenTimer = () => {
  // Latest update: Remove AutoLogout from this function (The AutoLogout uses React Redux)
  let expiredToken = localStorage.getItem('expiredToken')
      ? moment(localStorage.getItem('expiredToken'))
      : null;

  setInterval(async () => {
    expiredToken = localStorage.getItem('expiredToken')
        ? moment(localStorage.getItem('expiredToken'))
        : null;
    await validateToken(expiredToken, async () => {
      expiredToken = localStorage.getItem('expiredToken')
          ? moment(localStorage.getItem('expiredToken'))
          : null;
    });
  }, 1000);
};

export const base64FileHeaderMapper = (fileBase64: any) => {
  let fileHeader = new Map();

  //get the first 3 char of base64
  fileHeader.set('/9j', 'jpg');
  fileHeader.set('iVB', 'png');
  fileHeader.set('Qk0', 'bmp');
  fileHeader.set('SUk', 'tiff');
  fileHeader.set('JVB', 'pdf');
  fileHeader.set('UEs', 'ofd');

  let res = '';

  fileHeader.forEach((v, k) => {
    if (k == fileBase64.substr(0, 3)) {
      res = v;
    }
  });

  if (res == '') {
    res = 'unknown file';
  }

  return 'data:image/' + res + ';base64,' + fileBase64;
};

export const downloadBase64 = (url: string) => {
  return HttpService.post(ApiService.icdc.module.file.downloadBase64, {
    Url: url
  }).then((resp) => {
    return resp;
  });
};

export const getSharepointCdnFile = (url: string) => {
  if (url === null || url === undefined) {
    return null;
  }
  if(url.includes(ApiService.AzureStorage?.CdnBaseUrl) || url.includes(ApiService.AzureStorage?.StorageBaseUrl) || url.includes('static/media')) {
    return url;
  }
  return ApiService.icdc.module.file.getSpCdnFile + '?uri=' + url;
};

export const getSharepointCdnFileMedia = (url: string) => {
  if (url === null || url === undefined) {
    return null;
  }
  if(url.includes(ApiService.AzureStorage?.CdnBaseUrl) || url.includes(ApiService.AzureStorage?.StorageBaseUrl)) {
    return url;
  }
  return ApiService.icdc.module.file.getSpCdnFileMedia + '?uri=' + url;
};

export const downloadFile = (filepath, filename) => {
  var xhr = new XMLHttpRequest();
  let url = filepath;
  if(!url.includes(ApiService.AzureStorage?.CdnBaseUrl) && !url.includes(ApiService.AzureStorage?.StorageBaseUrl)) {
    url = encodeURIComponent(url);
  }
  xhr.open('GET', getSharepointCdnFile(url), true);
  xhr.responseType = 'blob';
  xhr.onload = function () {
    var urlCreator = window.URL || window.webkitURL;
    var imageUrl = urlCreator.createObjectURL(this.response);
    var tag = document.createElement('a');
    tag.href = imageUrl;
    tag.download = filename;
    document.body.appendChild(tag);
    tag.click();
    document.body.removeChild(tag);
  };

  xhr.send();
};

export const uploadImage = (
  fileName: any,
  fileData: any,
  fileCategory: any
) => {
  return HttpService.post(ApiService.icdc.module.file.uploadImage, {
    FileName: fileName,
    FileData: fileData.replace(/^data:image\/[a-z]+;base64,/, ''),
    FileCategory: fileCategory
  }).then((resp) => {
    return resp;
  });
};

export const uploadImageTinymce = async (
  blobInfo: any,
  progress: any
): Promise<string> => {
  return new Promise(async function (resolve, reject) {
    await checkToken();

    const token = JSON.parse(originLocalStorage.getItem())?.token

    let xhr = new XMLHttpRequest();
    xhr.withCredentials = false;
    xhr.open('POST', ApiService.icdc.module.file.uploadFileTinymce);

    xhr.setRequestHeader(
      'Authorization',
      authorizationTypes.BEARER + ' ' + token
    );
    console.log(authorizationTypes.BEARER, token);

    xhr.upload.onprogress = function (e) {
      progress((e.loaded / e.total) * 100);
    };

    xhr.onload = function (e) {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const _location = JSON.parse(this.responseText).location;
          resolve(getSharepointCdnFile(_location));
        } else {
          console.error(xhr.statusText);
          reject(xhr.statusText);
        }
      } else {
        reject(xhr.statusText);
      }
    };

    xhr.onerror = function () {
      // failure('Image upload failed due to a XHR Transport error. Code: ' + xhr.status);
      reject(xhr.statusText);
    };

    let formData = new FormData();
    formData.append('fileCategory', 'Content');
    formData.append('files', blobInfo.blob(), blobInfo.filename());

    xhr.send(formData);
  });
};

export const validImage = (img) => {
  if (img.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|tif|tiff)$/)) {
    return true;
  } else {
    Swal.fire({
      text: `Image not valid, please using image format jpg, jpeg, png, gif, tif, or tiff`,
      title: `Image Validation`,
      icon: 'warning'
    });
    return false;
  }
};

export const uploadFile = (file: any) => {
  return HttpService.post(ApiService.icdc.module.file.uploadFile, file, 'multipart/form-data').then(
    (resp) => {
      return resp;
    }
  );
};

export const SupportingFileTypes = {
  image: '.gif,.jpg,.jpeg,.png,.tif,.tiff',
  video: '.mp4,.mov',
  doc: '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf'
};

export const getAzurePhoto = (instance, _account, success, error) => {
  const _request = { scopes: ['User.Read'], account: _account };

  instance
    .acquireTokenSilent(_request)
    .then((tokenResponse) => {
      doFetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        method: 'GET',
        data: null,
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + tokenResponse.accessToken
        },
        responseType: 'arraybuffer'
      })
        .then((resp) => {
          const contentType = resp.headers['content-type'];
          const base64Body = Buffer.from(resp.data, 'binary').toString(
            'base64'
          );
          const base64DataUri = 'data:' + contentType + ';base64,' + base64Body;
          if (typeof success == 'function') {
            success(base64DataUri);
          }
        })
        .catch(error);
    })
    .catch(async (err) => {
      console.log('error getAzurePhoto', err);
      if (typeof error == 'function') {
        error(err);
      }
      if (error instanceof InteractionRequiredAuthError) {
        return instance.acquireTokenPopup(_request);
      }
    });
};

export const showValidationMessage = (validationMessage) => {
  Swal.fire({
    html:
      validationMessage && validationMessage.length > 0
        ? getErrorMessageDisplay(validationMessage)
        : '',
    title: '',
    icon: 'warning'
  });
};

export const fetchGlair = async (
  url: string,
  requestOptions: Omit<AxiosRequestConfig, 'headers'>
) => {
  const tokenAccess = JSON.parse(
    localStorage.getItem(`msal.token.keys.${ApiKeys.azureAD_client_id}`)
  );
  const idTokenAccess = tokenAccess.idToken[0];

  const idToken = JSON.parse(localStorage.getItem(idTokenAccess)).secret;
  if (!idToken) return;

  const res = await doFetch(url, {
    ...requestOptions,
    headers: ApiService.talent.getHeaderAuth(idToken)
  });

  if (res.statusCode === 401) {
    Swal.fire({
      html: 'An error has occured, please try again later.',
      title: res.messages.join(),
      icon: 'warning'
    }).then((result) => {
      if (result.value) {
        window.location.replace('/logout');
      }
    });
  }

  if (res.errorID) {
    throw new Error(res.messages.join('\n'));
  }

  return res.data;
};
